 import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Observable,
  tap,
  switchMap,
} from 'rxjs';
import { EventInput } from '@fullcalendar/core';
import { DateTime } from 'luxon';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { CalendarView } from '../model/calendar.view';
import {
  stripTimeZone,
} from '@shared/utilities/utilities';
import { MessageService } from 'primeng/api';
import { ActivityRecordService } from '@shared/services/activity-record/activity-record.service';
import { SignalsService } from '@shared/services/signals/signals.service';
import { ActivitySummaryResponse } from '@shared/models/activity-summary/activity-summary.response';
import { Sequences } from '@shared/models/activity-summary/sequences';
import { SummaryPeriod } from '@shared/models/summary-period';
import { ErrorLogService } from '@shared/services/errorhandler/error-log.service';

interface PrevSelectedMonth {
  date: SummaryPeriod;
  timestamp: string;
}

@Injectable({
  providedIn: 'root',
})
export class CalendarService {
  private readonly SEQ_COLOR = 'var(--p-primary-600)';
  private readonly MISC_COLOR = '#f1a557';

  // Selected month as ISO date string (yyyy-mm-dd). Updated when the user selects a month on the calendar.
  // Triggers calendarEvents$ to update. Starts with today's date.
  private _selectedMonth = new BehaviorSubject<SummaryPeriod | null>(null);
  
  get selectedMonth(): SummaryPeriod | null {
    return this._selectedMonth.getValue();
  }

  // Triggers a manual refresh.
  private _forceRefresh = false;

  // Persistent settings/actions.
  private readonly STORAGE_KEYS = {
    prevSelectedMonth: 'calendar.prevSelectedMonth',
  };

  selectedMonth$ = this._selectedMonth.asObservable();
  empNumber: string = '';
  lastUpdated?: string;

  constructor(
    private activityRecordService: ActivityRecordService,
    private messageService: MessageService,
    private signalService: SignalsService,
    private errorLogService: ErrorLogService
  ) { }

  calendar$ = this.signalService.employeeNumber$.pipe(
    switchMap((empNum) => {

      this.empNumber = empNum;
      return this.createCalendarView();
    })
  );


  getIntialDate(): SummaryPeriod {
    const initialPeriod: SummaryPeriod = {
      year: DateTime.now().year,
      period: DateTime.now().month,
    };
    // Previously selected month. So that when navigating back to the calendar,
    // the user is taken back to the month they were viewing.
    const prevSelectedMonth = localStorage.getItem(
      this.STORAGE_KEYS.prevSelectedMonth
    );

    if (prevSelectedMonth) {
      const selectedMonth = JSON.parse(prevSelectedMonth) as PrevSelectedMonth;

      // Only use previously saved month if the timestamp is recent (less than two days ago).
      // This ensures the user is taken to the current month if they haven't opened the app
      // for a while. diffNow() ends up negative, hence the * -1.
      const age =
        DateTime.fromISO(selectedMonth.timestamp).diffNow('days').days * -1;
      if (age < 2) {
        return selectedMonth.date;
      }
    }

    //! FIX ME - REVIEW THIS WHEN ABLE
    // else {
    //   localStorage.setItem(this.STORAGE_KEYS.prevSelectedMonth, JSON.stringify({
    //     date: initialPeriod,
    //     timestamp: DateTime.now().toISO(),
    //   }));
    //   return initialPeriod;
    // }
    return initialPeriod;
  }

  saveSelectedMonth(date: SummaryPeriod): void {
    const selectedMonth: PrevSelectedMonth = {
      date,
      timestamp: DateTime.now().toISO(),
    };
    localStorage.setItem(
      this.STORAGE_KEYS.prevSelectedMonth,
      JSON.stringify(selectedMonth)
    );
  }

  /** Updates selected month observable. Set when the calendar month is changed. */
  setSelectedMonth(date: SummaryPeriod): void {
    this.saveSelectedMonth(date);
    this._selectedMonth.next(date);
  }

  /** Use a consistent day for request caching. */
  // private normalizeSelectedMonth(selectedMonth: string): SummaryPeriod {
  //   return DateTime.fromISO(selectedMonth).set({ day: 15 }).toISODate() || "";
  // }

  forceRefresh(): void {
    this._forceRefresh = true;
    const selectedMonth = this._selectedMonth.getValue();
    if (selectedMonth) {
      this.setSelectedMonth(selectedMonth);
    }
  }

  private createCalendarView(): Observable<RequestState<CalendarView>> {
    
    return combineLatest([
      this.getActivityRecordCurrentMonth(),
      this.getActivityRecordPrevMonth(),
      this.getActivityRecordNextMonth(),
    ]).pipe(
      map(
        ([
          currentActivityRecordState,
          prevMonthActivityRecordState,
          nextMonthActivityRecordState,
        ]) => {
          // Pass on other state (isLoading, hasError) if no response. Only uses selected month activity
          // record for loading/error state.
          if (currentActivityRecordState.hasError) {
            this.showErrorAlert(currentActivityRecordState.status);
          }
          
          const activitySummary = currentActivityRecordState.response;
  
          const thisMonthSeqEvents = this.createSequenceEvents(activitySummary);
          //let thisMonthNonSeqEvents = this.createNonSequenceEvents(activityRecord);

          //const monthlyTotals = this.getMonthlyTotals(activityRecord);

          // Prev month 1 events
          // let prevMonthOneNonSeqEvents: EventInput[] = [];
          let prevMonthSeqEvents: EventInput[] = [];
          if (prevMonthActivityRecordState.response) {
            prevMonthSeqEvents = this.createSequenceEvents(
              prevMonthActivityRecordState.response
            );
            // prevMonthNonSeqEvents = this.createNonSequenceEvents(
            //   prevActivityRecordState.response
            // );
          }

          // Prev month 2 events
          // let prevMonthTwoNonSeqEvents: EventInput[] = [];
          let nextMonthSeqEvents: EventInput[] = [];
          if (nextMonthActivityRecordState.response) {
            nextMonthSeqEvents = this.createSequenceEvents(
              nextMonthActivityRecordState.response
            );
            // nextMonthNonSeqEvents = this.createNonSequenceEvents(
            //   nextActivityRecordState.response
            // );
          }

          const selectedMonthDate = DateTime.fromISO(
            this.createDateStringFrom(this._selectedMonth.value || null));

          const prevMonth = selectedMonthDate.minus({ months: 1 });
          const nextMonth = selectedMonthDate.plus({ months: 1 });

          const holidayEventsThisMonth = this.createHolidayPayEvents(
            selectedMonthDate.month,
            selectedMonthDate.year
          );
          const holidayEventsPrevMonthOne = this.createHolidayPayEvents(
            prevMonth.month,
            prevMonth.year
          );
          const holidayEventsPrevMonthTwo = this.createHolidayPayEvents(
            nextMonth.month,
            nextMonth.year
          );


          // Combine and filter prev month, this month, and next month events.
          // const nonSeqEvents = [
          //   ...thisMonthNonSeqEvents,
          //   ...prevMonthOneNonSeqEvents,
          //   ...prevMonthTwoNonSeqEvents,
          // ];
         
          const seqEvents = [
            ...thisMonthSeqEvents,
            ...prevMonthSeqEvents,
            ...nextMonthSeqEvents,
          ];

          // let events = this.combineAndFilterEvents(
          //   seqEvents
          // );

          let events = [
            ...seqEvents,
            ...holidayEventsThisMonth,
            ...holidayEventsPrevMonthOne,
            ...holidayEventsPrevMonthTwo,
          ];

          const calendarView: CalendarView = {
            events,
            //monthlyTotals,
            timestamp: DateTime.fromISO(activitySummary?.timestamp || '', {
              zone: 'utc',
            }),
          };
          // Return response with other state passed through.
          return currentActivityRecordState.into<CalendarView>(calendarView);
        }
      )
    );
  }

  loadLastUpdted(date: string): string | undefined {
    const dateNow = DateTime.now();
    const dateLastUpdated = DateTime.fromISO(date, { zone: 'utc' });
    const diffMinutes = dateNow.diff(dateLastUpdated, 'minutes').minutes;

    if (diffMinutes < 1) {
      return 'Just now';
    }
    else if (typeof diffMinutes != 'number') {
      return 'unknown';
    }
    else if (diffMinutes > 1) {
      return `${Math.floor(diffMinutes)} minutes ago`;
    }
    else {
      return '';
    }
  }

  createDateStringFrom(summaryPeriod: SummaryPeriod | null): string {
    if (!summaryPeriod) {
      return ''
    }
    const year = summaryPeriod.year;

    const period =
      summaryPeriod.period < 10 ?
        `0${summaryPeriod.period}` :
        summaryPeriod.period;

    const day = '15';
    return `${year}-${period}-${day}`;
  }

  getActivityRecordCurrentMonth(): Observable<
    RequestState<ActivitySummaryResponse>
  > {
    return this.selectedMonth$.pipe(
      filter((selectedMonth) => selectedMonth !== null),
      switchMap((selectedMonth) => {

        if (this._forceRefresh) {
          this._forceRefresh = false;

          return this.activityRecordService
            .getSummaryRecords(
              this.empNumber,
              selectedMonth!.year,
              selectedMonth!.period,
              !this._forceRefresh
            )
            .pipe(
              tap((activityRecordState) => {
                if (activityRecordState.hasError) {
                  this.showErrorAlert(activityRecordState.status);
                }
              })
            );
        } else {
          return this.activityRecordService
            .getSummaryRecords(
              this.empNumber,
              selectedMonth!.year,
              selectedMonth!.period
            )
            .pipe(
              tap((activityRecordState) => {
                if (activityRecordState.hasError) {
                  this.showErrorAlert(activityRecordState.status);
                }
              })
            );
        }
      })
    );
  }

  private getActivityRecordPrevMonth(): Observable<
    RequestState<ActivitySummaryResponse>
  > {
    return this.selectedMonth$.pipe(
      filter((selectedMonth) => selectedMonth !== null),
      switchMap((selectedMonth) => {
        if (this._forceRefresh) {
          this._forceRefresh = false;

          const prevMonth = this.getSelectedPeriod(selectedMonth!, 'prev');

          return this.activityRecordService
            .getSummaryRecords(
              this.empNumber,
              prevMonth!.year,
              prevMonth!.period,
              !this._forceRefresh
            )
            .pipe(
              tap((activityRecordState) => {
                if (activityRecordState.hasError) {
                  this.showErrorAlert(activityRecordState.status);
                }
              })
            );
        } else {
          const prevMonth = this.getSelectedPeriod(selectedMonth!, 'prev');

          return this.activityRecordService
            .getSummaryRecords(
              this.empNumber,
              prevMonth!.year,
              prevMonth!.period,
            )
            .pipe(
              tap((activityRecordState) => {
                if (activityRecordState.hasError) {
                  this.showErrorAlert(activityRecordState.status);
                }
              })
            );
        }
      })
    );
  }

  private getActivityRecordNextMonth(): Observable<
    RequestState<ActivitySummaryResponse>
  > {
    return this.selectedMonth$.pipe(
      filter((selectedMonth) => selectedMonth !== null),
      switchMap((selectedMonth) => {
        if (this._forceRefresh) {
          this._forceRefresh = false;

          const nextMonth = this.getSelectedPeriod(selectedMonth!);

          return this.activityRecordService
            .getSummaryRecords(
              this.empNumber,
              nextMonth!.year,
              nextMonth!.period,
              !this._forceRefresh
            )
            .pipe(
              tap((activityRecordState) => {
                if (activityRecordState.hasError) {
                  this.showErrorAlert(activityRecordState.status);
                }
              })
            );
        } else {
          const nextMonth = this.getSelectedPeriod(selectedMonth!);

          return this.activityRecordService
            .getSummaryRecords(
              this.empNumber,
              nextMonth!.year,
              nextMonth!.period,
            )
            .pipe(
              tap((activityRecordState) => {
                if (activityRecordState.hasError) {
                  this.showErrorAlert(activityRecordState.status);
                }
              })
            );
        }
      })
    );
  }

  private createSequenceEvents(
    summaryResponse?: ActivitySummaryResponse | null
  ): EventInput[] {
    const events: EventInput[] = [];

    if (!summaryResponse) {
      return events;
    }

    for (const sequence of summaryResponse.sequences) {
      // if (
      //   ((item.activityType === 'SEQ' && item.removalCode.trim() === '') ||
      //     item.activityType === 'CRR') //&& !this.isTrainingRemovalAndAdd(item, activityRecord)
      // ) {
      events.push(this.createEventFromSequence(sequence));
      // }
    }
    return events;
  }

  /** Returns a calendar event from the sequence information. */
  private createEventFromSequence(sequenceItem: Sequences): EventInput {
    const position = sequenceItem.seat;

    // If sequence data exists, the start and end time are more accurate and will be used.
    // If sequence data is missing, use the less accurate activity start and end times.
    if (!sequenceItem || sequenceItem.dutyPeriodSummaries.length === 0) {
      return []; //this.createEventFromSequenceWithMissingSeqData(activityItem);
    }


    const dutyPeriods = sequenceItem.dutyPeriodSummaries;

    // Use duty period start/end times for more accurate times.
    if (dutyPeriods.length > 0) {
      const firstDutyPeriod = dutyPeriods[0];
      const lastDutyPeriod = dutyPeriods[dutyPeriods.length - 1];

      if (!firstDutyPeriod.startTime || !lastDutyPeriod.endTime) {
        return {};
      }

      let sequenceStartTime = DateTime.fromISO(
        firstDutyPeriod.startTime.local || '',
        { zone: 'utc' }
      );
      let sequenceEndTime = DateTime.fromISO(lastDutyPeriod.endTime.local || '', {
        zone: 'utc'
      }
      );

      // For credited removals, accurate start/end times aren't important. It's better to display the original
      // start/end times from the activity data.
      // if (activityItem.activityType === 'CRR') {
      //   sequenceStartTime = DateTime.fromISO(activityItem.startDateTimeLocal, {
      //     zone: 'utc',
      //   });
      //   sequenceEndTime = DateTime.fromISO(activityItem.endDateTimeLocal, {
      //     zone: 'utc',
      //   });
      // }



      // To keep consistent, pad starting zeroes to sequence number.
      let title = sequenceItem.sequenceNumber.toString().padStart(5, '0');
      const sequence = { sequenceComplete: true };
      let backgroundColor = this.SEQ_COLOR;

      // For 777/787 FOs, append seat (FO/FB/FC) to title.
      if (
        sequenceItem.sequenceNumber &&
        (position === 'FB' || position === 'FC')
      ) {
        title += ` ${position}`;
      }

      // if (removeCode !== '') {
      //   title = removeCode;
      //   backgroundColor = this.MISC_COLOR;
      // }

      const startAndEndMarginClasses = this.getEventMarginClass(
        sequenceStartTime,
        sequenceEndTime
      );

      //const _sequenceItem = this.reversedLegsForSequence(sequenceItem);

      return {
        title,
        start: stripTimeZone(sequenceStartTime.toISO() || ''),
        // FullCalendar excludes the end day, so add another day to display correctly.
        end: stripTimeZone(sequenceEndTime.plus({ days: 1 }).toISO() || ''),
        allDay: true,
        backgroundColor,
        // 'complete' class makes finished sequences faded. Make sure title is long enough to be a sequence.
        // Use for future if displaying seq that is not completed yet.  
        // className: `${title && title.length > 2 ? 'complete' : ''}
        //    ${startAndEndMarginClasses.startClass} 
        //    ${startAndEndMarginClasses.endClass}
        //    `,
        className: `
           ${startAndEndMarginClasses.startClass} 
           ${startAndEndMarginClasses.endClass}
           `,
        extendedProps: {
          sequenceNumber: sequenceItem.sequenceNumber,
          position: position,
          sequence: sequenceItem,
          // The date that will be used for sequence request needs to match date from activity record.
          startDate: sequenceItem.originationDate,
        }
      };
    }

    return {};
  }

  getSelectedPeriod(
    summaryPeriod: SummaryPeriod,
    adjacent?: string
  ): SummaryPeriod {
    if (adjacent == 'prev') {
      if (summaryPeriod?.period === 1) {
        const updatedPeriod = {
          year: summaryPeriod.year - 1,
          period: 12,
        };
        return updatedPeriod;
      }
      const updatedPeriod = {
        year: summaryPeriod.year,
        period: summaryPeriod.period - 1,
      };
      return updatedPeriod;
    } else {
      if (summaryPeriod.period === 12) {
        const updatedPeriod = {
          year: summaryPeriod.year + 1,
          period: 1,
        };
        return updatedPeriod;
      }
      const updatedPeriod = {
        year: summaryPeriod.year,
        period: summaryPeriod.period + 1,
      };
      return updatedPeriod;
    }
  }

  private showErrorAlert(status: number): void {
    this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: Calendar service: ${RequestStatus[status]}`);
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: `Unable to load calendar information. Status: ${RequestStatus[status]} `,
    });
  }

  private getEventMarginClass(
    eventStartTime: DateTime,
    eventEndTime: DateTime
  ): { startClass: string; endClass: string } {
    // Don't shift event display if it's a one day trip.
    if (eventEndTime.diff(eventStartTime).as('hours') < 24) {
      return { startClass: '', endClass: '' };
    }

    // These values will need changed if the current padding or max width changes.
    const MAX_CALENDAR_WIDTH = 700;
    const PADDING = 32;

    const screenWidth = window.innerWidth;

    // The calendar will be a max of 500px wide, but can be smaller on
    // smaller screens.
    const calendarWidth = Math.min(screenWidth, MAX_CALENDAR_WIDTH);

    // The width of a single calendar day.
    const dayCellWidth = (calendarWidth - PADDING) / 7;

    const startDayRatio = eventStartTime.hour / 24; // 11/8 03:25 -> 3 / 24 = 0.125
    const endDayRatio = (24 - eventEndTime.hour) / 24; // Want the amount of day left. 11/9 12:05

    // Calculate offset and use closest multiple of 5.
    const leftMargin = Math.round((startDayRatio * dayCellWidth) / 5) * 5;
    const rightMargin = Math.round((endDayRatio * dayCellWidth) / 5) * 5;

    return {
      startClass: `left-margin-${leftMargin}`,
      endClass: `right-margin-${rightMargin}`,
    };
  }

  /** Returns an array of events that provide holiday pay. */
  private createHolidayPayEvents(month: number, year: number): EventInput[] {
    const premiumDates: EventInput[] = [];

    // For December: Christmas Eve, Christmas, New Year's Eve.
    if (month === 12) {
      premiumDates.push({
        display: 'background',
        allDay: true,
        start: `${year}-12-24`,
      });

      premiumDates.push({
        display: 'background',
        allDay: true,
        start: `${year}-12-25`,
      });

      premiumDates.push({
        display: 'background',
        allDay: true,
        start: `${year}-12-31`,
      });
    }

    // For January: New Year's Day.
    if (month === 1) {
      premiumDates.push({
        display: 'background',
        allDay: true,
        start: `${year}-01-01`,
      });
    }

    // July 4th.
    if (month === 7) {
      premiumDates.push({
        display: 'background',
        allDay: true,
        start: `${year}-07-04`,
      });
    }

    // Memorial Day. Need to find last Monday of May.
    if (month === 5) {
      // Iterate backward through days, Memorial day is the first Monday found.
      for (let i = 31; i > 0; i--) {
        const date = DateTime.fromObject({ year, month, day: i });

        if (date.weekday === 1) {
          premiumDates.push({
            display: 'background',
            allDay: true,
            start: `${year}-05-${date.day}`,
          });
          break;
        }
      }
    }

    // For November:
    // - Day before Thanksgiving.
    // - Thanksgiving.
    // - The Sunday following Thanksgiving Day.
    if (month === 11) {
      // Find the fourth Thursday to identify Thanksgiving.

      // Start on the first.
      let date = DateTime.fromObject({ year, month, day: 1 });

      // Find first Thursday.
      let isThursday = false;

      while (!isThursday) {
        if (date.weekday === 4) {
          isThursday = true;
        } else {
          date = date.plus({ days: 1 });
        }
      }

      const thanksgiving = date.plus({ weeks: 3 });

      // Create events.
      premiumDates.push({
        display: 'background',
        allDay: true,
        start: `${year}-${month}-${thanksgiving.minus({ days: 1 }).day}`,
      });

      premiumDates.push({
        display: 'background',
        allDay: true,
        start: `${year}-${month}-${thanksgiving.day}`,
      });

      // Sunday after Thanksgiving can potentially be in December.
      const sunAfterThanks = thanksgiving.plus({ days: 3 });
      premiumDates.push({
        display: 'background',
        allDay: true,
        start: `${year}-${sunAfterThanks.month}-${sunAfterThanks.day
          .toString()
          .padStart(2, '0')}`,
      });
    }

    return premiumDates;
  }

  /** Combines events, remove any duplicates, and filter based on showDfps. */
  // private combineAndFilterEvents(
  //   sequenceEvents: EventInput[],
  //   nonSequenceEvents: EventInput[],
  //   showDfps: boolean
  // ): EventInput[] {
  //   const uniqueEvents = this.appendUniqueNonSequenceEvents(
  //     sequenceEvents,
  //     //nonSequenceEvents
  //   );

  //   const events: EventInput[] = uniqueEvents.filter((event) => {
  //     if (event.title && event.title !== '') {
  //       if (!showDfps) {
  //         return (
  //           event.title !== '24' &&
  //           event.title !== 'M2' &&
  //           event.title !== 'G2' &&
  //           event.title !== 'X2'
  //         );
  //       } else {
  //         return true;
  //       }
  //     }
  //     return false;
  //   });

  //   return events;
  // }


  /** Create observable of calendar events for activities other than sequences. */
  // private createNonSequenceEvents(
  //   activityRecord?: LogbookActivityRecordResponse | null
  // ): EventInput[] {
  //   if (!activityRecord) {
  //     return [];
  //   }

  //   // Use hashmap with date index to ensure no duplicate events per day. 24s are occasionally duplicated.
  //   const events: { [key: string]: EventInput } = {};

  //   // const dutyArrayEvents = this.createEventsFromDutyArray(activityRecord);

  //   // for (const dutyArrayEvent of dutyArrayEvents) {
  //   //   events[dutyArrayEvent.start?.toString() || ''] = dutyArrayEvent;
  //   // }

  //   for (const item of activityRecord.activityItems) {
  //     if (
  //       item.activityType !== 'SEQ' &&
  //       !item.linkedSequence &&
  //       item.activityType !== 'MSC' &&
  //       item.activityType !== 'PRV' &&
  //       item.removalCode.trim() === ''
  //     ) {
  //       const startTime = DateTime.fromISO(item.originationDate, {
  //         zone: 'utc',
  //       });
  //       const endTime = this.getEndDateTime(
  //         item.originationDate,
  //         this.convertDuration(item.duration)
  //       ).minus({ minutes: 1 }); // Subtract one minute so that 24 hr events display properly.

  //       const startAndEndMarginClasses = this.getEventMarginClass(
  //         startTime,
  //         endTime
  //       );

  //       // If an event already exists for this date, don't overwrite if the new event is a 24.
  //       if (
  //         !(
  //           // substring grabs the date portion of ISO string, disregarding time if it exists.
  //           (
  //             events[item.originationDate.substring(0, 10)] &&
  //             item.activityIdentifier === 24
  //           )
  //         )
  //       ) {
  //         events[item.originationDate.substring(0, 10)] = {
  //           id: `${item.originationDate} ${item.activityType}`,
  //           title: this.convertDisplayedActivityType(item.activityType),
  //           start: item.originationDate,
  //           end: endTime.plus({ days: 1 }).toISO(),
  //           allDay: true,
  //           backgroundColor: this.DFP_COLOR,
  //           className: `${startAndEndMarginClasses.startClass} ${startAndEndMarginClasses.endClass}`,
  //         };
  //       }
  //     }
  //   }

  //   const eventsArray = Object.keys(events).map((date) => events[date]);

  //   return eventsArray;
  // }
}
